<template>
    <Modal title="ЕЛС достава" @close="$emit('update:show', false)">
        <Loading variant="is-white" height="4" v-if="loading"/>
        <div class="notification is-danger is-light" v-else-if="loadError">{{ loadError }}</div>

        <!-- Веќе испратена нарачка - формата се крие. -->
        <div v-else-if="booked">
            <div class="notification is-success is-light has-text-centered">
                <p class="heading">Број за следење</p>
                <p class="title is-4 mb-2">{{ booked.tracking_number }}</p>
                <button type="button" class="button is-small is-light" @click="copyTracking">
                    <span class="icon"><i class="fa fa-copy"></i></span>
                    <span>Копирај</span>
                </button>
            </div>

            <table class="table is-fullwidth is-narrow is-size-7">
                <tbody>
                <!-- Пратката ја држи сметката од која е нарачана - следењето,
                     откажувањето и етикетите одат преку неа. -->
                <tr v-if="accounts.length > 1 && booked.account_label">
                    <td class="has-text-grey">ЕЛС сметка</td>
                    <td class="has-text-right">{{ booked.account_label }}</td>
                </tr>
                <tr v-if="booked.address">
                    <td class="has-text-grey">Адреса</td>
                    <td class="has-text-right">{{ booked.address }}</td>
                </tr>
                <tr>
                    <td class="has-text-grey">Тежина</td>
                    <td class="has-text-right">{{ booked.weight }} кг.</td>
                </tr>
                <tr>
                    <td class="has-text-grey">Поштарина</td>
                    <td class="has-text-right">{{ booked.price }} ден.</td>
                </tr>
                <tr>
                    <td class="has-text-grey">Откуп</td>
                    <td class="has-text-right">{{ booked.cod }} ден.</td>
                </tr>
                <tr>
                    <td class="has-text-grey">Плаќа</td>
                    <td class="has-text-right">Примач</td>
                </tr>
                <tr>
                    <td class="has-text-grey">Испратена на</td>
                    <td class="has-text-right">{{ booked.booked_at }}</td>
                </tr>
                </tbody>
            </table>

            <hr class="my-3">
            <ElsStatus :orderId="order.id"/>

            <div class="notification is-danger mt-3" v-if="confirmVoid">
                <template v-if="!voidConflict">
                    <p class="mb-2">
                        Пратката ќе биде откажана кај ЕЛС и врската со нарачката ќе биде избришана.
                    </p>
                    <div class="buttons is-right mb-0">
                        <button type="button" class="button is-small" @click="confirmVoid = false">Назад</button>
                        <button type="button" class="button is-small is-danger is-light"
                                :class="{'is-loading': voiding}" @click="voidBooking(false)">
                            Откажи ја пратката
                        </button>
                    </div>
                </template>
                <template v-else>
                    <p class="mb-2"><b>ЕЛС не ја откажа пратката.</b></p>
                    <p class="mb-2">{{ voidConflict }}</p>
                    <p class="mb-2">
                        Поништете ја во ЕЛС порталот, потоа отповрзете ја тука - отповрзувањето само ја брише
                        врската кај нас.
                    </p>
                    <div class="buttons is-right mb-0">
                        <button type="button" class="button is-small"
                                @click="confirmVoid = false; voidConflict = null">Назад
                        </button>
                        <button type="button" class="button is-small is-danger"
                                :class="{'is-loading': voiding}" @click="voidBooking(true)">
                            Ја поништив во ЕЛС - отповрзи
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <!-- Форма за нарачување. Се внесуваат само тежина и откуп; сè друго се избира. -->
        <div v-else>
            <div class="notification is-light py-2 px-3 mb-3">
                <p class="is-size-5 has-text-weight-bold mb-1">{{ customer.name || "-" }}</p>
                <p class="mb-0">{{ selectedAddress.label || "-" }}</p>
                <p class="is-italic mb-0" v-if="selectedAddress.description">
                    {{ selectedAddress.description }}
                </p>
            </div>

            <!-- Од која сметка се испраќа. Ја одредува и адресата на подигање,
                 и ценовникот, па промената бара нова понуда. Се прикажува само
                 кога навистина има избор. -->
            <div class="field" v-if="accounts.length > 1">
                <label class="label">ЕЛС сметка</label>
                <div class="control">
                    <div class="select is-fullwidth">
                        <select v-model="form.account">
                            <option v-for="account in accounts" :key="account.key"
                                    :value="account.key" :disabled="!account.available">
                                {{ account.label }}{{ account.available ? "" : " (недостапна)" }}
                            </option>
                        </select>
                    </div>
                </div>
                <p class="help" v-if="pickupLine">Се подига од: {{ pickupLine }}</p>
                <p class="help is-danger" v-if="selectedAccount && !selectedAccount.available">
                    ЕЛС не ја прифати оваа сметка: {{ selectedAccount.error }}
                </p>
            </div>

            <!-- Телефон: првиот е главен, останатите одат во коментарот. -->
            <div class="field">
                <label class="label">Телефон</label>
                <div class="notification is-danger is-light py-2 is-size-7 mb-0" v-if="!customer.phones.length">
                    Корисникот нема запишан телефон. ЕЛС бара број за примачот - додајте го во картонот
                    на клиентот пред нарачување.
                </div>
                <div class="control">
                    <template v-for="phone in customer.phones">
                        <input class="is-checkradio" type="radio" name="els_phone" :key="phone"
                               :id="'els_phone_' + phone" :value="phone" v-model="form.phone">
                        <label :for="'els_phone_' + phone" :key="phone + '-label'">{{ phone }}</label>
                    </template>
                </div>
            </div>

            <!-- Адресата е таа на нарачката. Се нуди избор само ако клиентот
                 навистина има повеќе од една - инаку е список од еден ред. -->
            <div class="notification is-danger is-light py-2 is-size-7 mb-3"
                 v-if="!customer.addresses.length">
                Нарачката нема адреса.
            </div>
            <div class="field" v-else-if="customer.addresses.length > 1">
                <label class="label">Адреса</label>
                <div class="control" v-for="address in customer.addresses" :key="address.id">
                    <input class="is-checkradio" type="radio" name="els_address"
                           :id="'els_address_' + address.id" :value="address.id" v-model="form.address_id">
                    <label :for="'els_address_' + address.id">
                        {{ address.label }}
                        <span class="tag is-light is-small" v-if="address.id === customer.default_address_id">
                            од нарачката
                        </span>
                        <br>
                        <span class="has-text-grey is-size-7" v-if="address.description">
                            {{ address.description }}
                        </span>
                    </label>
                </div>
            </div>

            <!-- Како ЕЛС ќе ја добие адресата. Не се внесува - се пресметува од DNP адресата. -->
            <div class="field">
                <label class="label">ЕЛС адреса</label>

                <Loading variant="is-white" height="2" v-if="prefillLoading"/>

                <template v-else>
                    <div class="notification py-2 px-3 mb-0 is-flex is-align-items-center
                                is-justify-content-space-between"
                         :class="resolved ? 'is-success is-light' : 'is-warning'" v-if="!manual">
                        <span v-if="resolved">{{ deliveryLine }}</span>
                        <span v-else>
                            Адресата не се совпадна со ЕЛС регистарот - изберете место и улица рачно.
                        </span>
                        <button type="button" class="button is-small ml-2" @click="openManual" v-if="resolved">
                            Промени
                        </button>
                    </div>

                    <div v-if="manual">
                        <ElsSearchSelect label="Место" required url="/els/places"
                                         placeholder="Пребарај место"
                                         :value="place" :candidates="placeCandidates"
                                         :optionLabel="placeLabel"
                                         emptyText="Нема пронајдено место. Скопје постои само по општини (пр. Центар, Аеродром, Карпош)."
                                         @input="setPlace"/>

                        <!-- Улиците припаѓаат на место, па нема што да се бара без него. -->
                        <ElsSearchSelect label="Улица" required v-if="manualForm.place_id"
                                         :key="'street-' + manualForm.place_id"
                                         :url="'/els/places/' + manualForm.place_id + '/streets'"
                                         placeholder="Пребарај улица"
                                         :value="street" :candidates="streetCandidates"
                                         emptyText="Нема пронајдено улица во ова место."
                                         @input="setStreet"/>

                        <div class="columns is-mobile" v-if="manualForm.place_id">
                            <div class="column">
                                <div class="field">
                                    <label class="label">Улица бр.</label>
                                    <div class="control">
                                        <input class="input" type="text" placeholder="100" maxlength="32"
                                               v-model="manualForm.house_number">
                                    </div>
                                </div>
                            </div>
                            <div class="column">
                                <div class="field">
                                    <label class="label">Влез</label>
                                    <div class="control">
                                        <input class="input" type="text" maxlength="32"
                                               v-model="manualForm.entrance">
                                    </div>
                                </div>
                            </div>
                            <div class="column">
                                <div class="field">
                                    <label class="label">Стан</label>
                                    <div class="control">
                                        <input class="input" type="text" maxlength="32"
                                               v-model="manualForm.apartment">
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Бројот во DNP е едно поле ("22/5"), кај ЕЛС се три.
                             Суровата вредност се покажува за да се провери поделбата. -->
                        <p class="help">
                            Во DNP бројот е запишан како <b>{{ selectedAddress.street_number || "-" }}</b>
                            (број / влез / стан).
                            <a @click="closeManual" v-if="resolved">Врати се на автоматската адреса</a>
                        </p>
                    </div>
                </template>
            </div>

            <!-- Што има во нарачката - за да знае операторот колку пакети пакува. -->
            <div class="field" v-if="contents.sizes.length">
                <table class="table is-fullwidth is-narrow mb-0">
                    <tbody>
                    <tr v-for="(size, index) in contents.sizes" :key="index">
                        <td>
                            {{ size.name }}
                            <span class="has-text-grey" v-if="size.size">({{ size.size }})</span>
                        </td>
                        <td class="has-text-right"><b>{{ size.quantity }}</b></td>
                    </tr>
                    <tr v-if="contents.sizes.length > 1">
                        <td class="has-text-grey">Вкупно</td>
                        <td class="has-text-right"><b>{{ contents.quantity }}</b></td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <!-- Единствените полиња што операторот ги внесува. -->
            <div class="columns is-mobile">
                <div class="column">
                    <div class="field">
                        <label class="label">Пакети</label>
                        <div class="control">
                            <input class="input" type="number" min="1" max="8" v-model.number="form.packages">
                        </div>
                    </div>
                </div>
                <div class="column">
                    <div class="field">
                        <label class="label">Тежина <span class="has-text-danger">*</span></label>
                        <div class="control">
                            <input class="input" type="number" min="0.1" step="0.1" placeholder="1.00"
                                   v-model="form.weight">
                        </div>
                    </div>
                </div>
                <div class="column">
                    <div class="field">
                        <label class="label">Откуп</label>
                        <div class="control">
                            <input class="input" type="number" min="0" v-model.number="form.cod">
                        </div>
                    </div>
                </div>
            </div>
            <p class="help is-danger mb-2" v-if="codError">{{ codError }}</p>

            <ElsMoney :money="money" :items="quoteItems" :loading="quoteLoading" :error="quoteError"/>

            <!-- Го составува серверот - предупредувањето, останатите телефони и
                 забелешката на адресата - но операторот има последен збор. -->
            <div class="field mt-3">
                <label class="label">
                    Коментар за курирот
                    <a class="is-pulled-right has-text-weight-normal is-size-7"
                       v-if="commentDirty" @click="resetComment">Врати го автоматскиот текст</a>
                </label>
                <div class="control">
                    <textarea class="textarea" rows="3" maxlength="500"
                              :value="form.comment" @input="editComment($event.target.value)"></textarea>
                </div>
                <p class="help is-warning" v-if="commentDirty && !hasFragileNote">
                    <b>КРШЛИВО!!</b> ќе биде додадено автоматски - не плаќаме за услугата „Кршливо“ кај ЕЛС.
                </p>
            </div>

            <div class="notification is-danger is-light py-2" v-if="bookError">{{ bookError }}</div>
        </div>

        <template v-slot:footer>
            <button class="button is-success" v-if="!loading && !loadError && !booked"
                    :class="{'is-loading': booking}" :disabled="!canBook || booking" @click="book">
                Нарачај кај ЕЛС
            </button>
            <button class="button is-danger is-light" v-if="booked && !confirmVoid" @click="confirmVoid = true">
                Откажи ја пратката
            </button>
        </template>
    </Modal>
</template>

<script>
    import Loading from "../../Loading";
    import ElsSearchSelect from "./ElsSearchSelect";
    import ElsMoney from "./ElsMoney";
    import ElsStatus from "./ElsStatus";
    import {elsBook, elsError, elsOptions, elsQuote, elsShow, elsVoid} from "../../../api/els";

    const EMPTY_ADDRESS = {id: null, label: "", description: "", street_number: ""};

    // Го држи и серверот (ElsController::FRAGILE_NOTE) - тука е само за да знае
    // формата дали операторот го избришал.
    const FRAGILE_NOTE = "КРШЛИВО!!";

    export default {
        name: "ElsModal",
        components: {ElsStatus, ElsMoney, ElsSearchSelect, Loading},
        props: {
            order: {type: Object, required: true},
            show: Boolean
        },
        data() {
            return {
                loading: true,
                loadError: null,
                options: {defaults: {}, pickup: null, limits: {}, accounts: []},
                booked: null,
                customer: {name: null, phones: [], addresses: [], default_phone: null, default_address_id: null},
                // Колку од која големина има во нарачката - само за читање.
                contents: {quantity: 0, sizes: []},
                // Она што серверот го најде за избраната адреса.
                prefill: {},
                prefillLoading: false,
                // Единственото што операторот го одлучува.
                form: {
                    // Со која ЕЛС сметка се испраќа. Празно значи основната.
                    account: null,
                    address_id: null,
                    phone: "",
                    weight: "1.00",
                    cod: 0,
                    // Се чува во orders.packages при нарачување и оди во коментарот.
                    packages: 1,
                    comment: ""
                },
                // Додека е false, коментарот го составува серверот и се освежува
                // при промена на телефон или адреса. Штом операторот пишува, е негов.
                commentDirty: false,
                // Се праќа само кога операторот рачно ја поправа ЕЛС адресата.
                manual: false,
                manualForm: {
                    place_id: null,
                    place_name: "",
                    street_id: null,
                    street_name: "",
                    house_number: "",
                    entrance: "",
                    apartment: ""
                },
                place: null,
                street: null,
                placeCandidates: [],
                streetCandidates: [],
                money: null,
                quoteItems: [],
                quoteDelivery: null,
                quoteComment: null,
                quoteLoading: false,
                quoteError: null,
                quoteTimeout: null,
                quoteCancel: null,
                booking: false,
                bookError: null,
                confirmVoid: false,
                voidConflict: null,
                voiding: false
            }
        },
        computed: {
            accounts() {
                return this.options.accounts || [];
            },
            selectedAccount() {
                return this.accounts.find(a => a.key === this.form.account) || null;
            },
            // Складот од кој курирот ја подига пратката - зависи од сметката.
            pickupLine() {
                const pickup = this.selectedAccount && this.selectedAccount.pickup;
                if (!pickup) return "";
                return this.composeLine(pickup.placeName, pickup.streetName, pickup.housenumber, "", "");
            },
            selectedAddress() {
                return this.customer.addresses.find(a => a.id === this.form.address_id) || EMPTY_ADDRESS;
            },
            // Дали ЕЛС адресата е решена - автоматски или рачно.
            resolved() {
                return this.manual
                    ? !!(this.manualForm.place_id && this.manualForm.street_id)
                    : !!this.prefill.resolved;
            },
            // Она што серверот последно го потврди; додека нема понуда, погледот од prefill.
            deliveryLine() {
                if (this.quoteDelivery) return this.quoteDelivery.line;
                const p = this.prefill;
                if (!p.place || !p.street) return "";
                return this.composeLine(p.place.name, p.street.name, p.house_number, p.entrance, p.apartment);
            },
            hasFragileNote() {
                return this.form.comment.indexOf(FRAGILE_NOTE) !== -1;
            },
            codError() {
                const max = this.options.limits.max_declared_value;
                if (max && Number(this.form.cod) > max)
                    return "Максималниот откуп е " + max + " ден.";
                return null;
            },
            canBook() {
                return !!this.form.address_id
                    && !!this.form.phone
                    && this.resolved
                    // Сметка што ЕЛС не ја прифаќа не може ни да понуди цена.
                    && (!this.selectedAccount || this.selectedAccount.available)
                    && Number(this.form.weight) > 0
                    && !this.codError;
            },
            // Коментарот не влијае на цената, па не е тука - инаку секој напишан
            // знак би барал нова понуда од ЕЛС.
            quoteKey() {
                return JSON.stringify(this.shipmentPayload());
            }
        },
        methods: {
            placeLabel(place) {
                let label = place.name;
                if (place.postal_code) label += " (" + place.postal_code + ")";
                if (place.municipality_name && place.municipality_name !== place.name)
                    label += " · " + place.municipality_name;
                return label;
            },
            composeLine(place, street, houseNumber, entrance, apartment) {
                let line = (street + " " + (houseNumber || "")).trim();
                if (entrance) line += ", влез " + entrance;
                if (apartment) line += ", стан " + apartment;
                return line + ", " + place;
            },
            setPlace(place) {
                this.place = place;
                this.manualForm.place_id = place ? place.id : null;
                this.manualForm.place_name = place ? place.name : "";
                // Улиците зависат од местото.
                this.setStreet(null);
                this.streetCandidates = [];
            },
            setStreet(street) {
                this.street = street;
                this.manualForm.street_id = street ? street.id : null;
                this.manualForm.street_name = street ? street.name : "";
            },
            openManual() {
                this.manual = true;
            },
            closeManual() {
                this.manual = false;
                this.seedManual();
            },
            /**
             * Рачната форма почнува од она што серверот го најде, за операторот да
             * поправа наместо да внесува од почеток.
             */
            seedManual() {
                const p = this.prefill;
                this.place = p.place || null;
                this.street = p.street || null;
                this.placeCandidates = p.place_candidates || [];
                this.streetCandidates = p.street_candidates || [];
                this.manualForm = {
                    place_id: p.place ? p.place.id : null,
                    place_name: p.place ? p.place.name : "",
                    street_id: p.street ? p.street.id : null,
                    street_name: p.street ? p.street.name : "",
                    house_number: p.house_number || "",
                    entrance: p.entrance || "",
                    apartment: p.apartment || ""
                };
            },
            editComment(value) {
                this.form.comment = value;
                this.commentDirty = true;
            },
            /**
             * quoteComment не служи тука - додека формата е "валкана" серверот го
             * враќа токму напишаниот текст. Затоа се бара нова понуда без коментар:
             * одговорот носи свежо составен текст за тековниот телефон и адреса.
             */
            resetComment() {
                this.commentDirty = false;
                this.form.comment = this.prefill.comment || FRAGILE_NOTE;
                this.runQuote();
            },
            /**
             * Сè што ја одредува пратката и цената. Примачот, бројот на пакети,
             * платачот и услугите ги поставува серверот - фронтендот не ги ни знае.
             */
            shipmentPayload() {
                const body = {
                    account: this.form.account,
                    address_id: this.form.address_id,
                    phone: this.form.phone,
                    weight: String(this.form.weight),
                    cod: Number(this.form.cod) || 0,
                    packages: Number(this.form.packages) || 1
                };
                if (this.manual) Object.assign(body, this.manualForm);
                return body;
            },
            /**
             * Коментарот се праќа само откако операторот ќе го допре. Додека не е
             * допрен, серверот го составува - па промената на телефон или адреса
             * го освежува сама.
             */
            payload() {
                const body = this.shipmentPayload();
                if (this.commentDirty) body.comment = this.form.comment;
                return body;
            },
            load() {
                this.loading = true;
                this.loadError = null;
                Promise.all([elsOptions(), elsShow(this.order.id)])
                    .then(([options, data]) => {
                        this.options = options;
                        this.booked = data.booked || null;
                        this.customer = data.customer || this.customer;
                        this.contents = data.contents || this.contents;
                        this.loading = false;
                        if (this.booked) return;
                        this.form.address_id = data.prefill.address_id;
                        this.form.phone = data.prefill.phone || "";
                        // Серверот ја предлага сметката - основната, или таа од
                        // претходна пратка ако нарачката веќе била испраќана.
                        this.form.account = data.prefill.account || this.options.defaults.account || null;
                        this.applyPrefill(data.prefill);
                    })
                    .catch(e => {
                        this.loadError = elsError(e);
                        this.loading = false;
                    });
            },
            applyPrefill(prefill) {
                this.prefill = prefill || {};
                this.form.weight = this.prefill.weight || this.options.defaults.weight || "1.00";
                this.form.cod = this.prefill.cod == null ? 0 : this.prefill.cod;
                this.form.packages = this.prefill.packages || 1;
                // Она што операторот веќе го напишал преживува промена на адреса.
                if (!this.commentDirty) this.form.comment = this.prefill.comment || FRAGILE_NOTE;
                this.manual = !this.prefill.resolved;
                this.seedManual();
                this.runQuote();
            },
            /**
             * ЕЛС совпаѓањето е по адреса, па менувањето адреса бара ново од серверот.
             */
            reloadPrefill() {
                this.prefillLoading = true;
                this.quoteDelivery = null;
                this.quoteComment = null;
                elsShow(this.order.id, {address_id: this.form.address_id, phone: this.form.phone})
                    .then(data => {
                        this.prefillLoading = false;
                        // Внесеното го задржува - операторот веќе го поставил, а
                        // ништо од тоа не зависи од адресата.
                        const kept = {
                            account: this.form.account,
                            weight: this.form.weight,
                            cod: this.form.cod,
                            packages: this.form.packages
                        };
                        this.applyPrefill(data.prefill);
                        Object.assign(this.form, kept);
                    })
                    .catch(e => {
                        this.prefillLoading = false;
                        this.loadError = elsError(e);
                    });
            },
            runQuote() {
                if (this.quoteCancel) this.quoteCancel.cancel();
                clearTimeout(this.quoteTimeout);
                if (!this.canBook) {
                    // Без валидни полиња нема цена - старата не смее да остане на екран.
                    this.quoteLoading = false;
                    this.money = null;
                    this.quoteItems = [];
                    return;
                }
                this.quoteLoading = true;
                this.quoteError = null;
                this.quoteTimeout = setTimeout(() => {
                    this.quoteCancel = this.$http.CancelToken.source();
                    elsQuote(this.order.id, this.payload(), {cancelToken: this.quoteCancel.token})
                        .then(data => {
                            if (!data) return; // откажано барање
                            this.money = data.money;
                            this.quoteItems = data.items || [];
                            this.quoteDelivery = data.delivery || null;
                            this.quoteComment = data.comment || null;
                            // Одговорот е составен за тековниот телефон, па го
                            // презема - освен ако операторот веќе не пишувал.
                            if (this.quoteComment && !this.commentDirty)
                                this.form.comment = this.quoteComment;
                            this.quoteLoading = false;
                        })
                        .catch(e => {
                            if (this.$http.isCancel(e)) return;
                            this.quoteError = elsError(e);
                            this.quoteLoading = false;
                        });
                }, 400);
            },
            book() {
                this.booking = true;
                this.bookError = null;
                elsBook(this.order.id, this.payload())
                    .then(data => {
                        this.booked = data.booked;
                        this.money = data.money;
                        this.booking = false;
                        this.$toast({message: "Нарачката е испратена кај ЕЛС", type: "is-success"});
                        this.$emit("saved");
                    })
                    .catch(e => {
                        this.booking = false;
                        this.bookError = elsError(e);
                        // 409 значи дека веќе е испратена - освежи, не повторувај.
                        if (e.response && e.response.status === 409) this.load();
                    });
            },
            voidBooking(force) {
                this.voiding = true;
                elsVoid(this.order.id, force)
                    .then(res => {
                        this.voiding = false;
                        this.confirmVoid = false;
                        this.voidConflict = null;
                        this.$toast({
                            message: res && res.cancelled_at_els === false
                                ? "Врската е избришана, но пратката НЕ е откажана кај ЕЛС"
                                : "Пратката е откажана кај ЕЛС",
                            type: res && res.cancelled_at_els === false ? "is-warning" : "is-success"
                        });
                        this.$emit("saved");
                        this.load();
                    })
                    .catch(e => {
                        this.voiding = false;
                        // 409 - ЕЛС одби, пратката најверојатно е веќе подигната.
                        if (e.response && e.response.status === 409) this.voidConflict = elsError(e);
                        else this.$toast({message: elsError(e), type: "is-danger"});
                    });
            },
            copyTracking() {
                this.$copyText(this.booked.tracking_number).then(() => {
                    this.$toast({message: "Бројот за следење е копиран", type: "is-success"});
                });
            }
        },
        watch: {
            "form.address_id"(value, previous) {
                if (previous === null || value === previous) return;
                // Рачната поправка важи за старата адреса - местото и улицата
                // не смеат да преживеат промена на адресата.
                this.manual = false;
                this.reloadPrefill();
            },
            quoteKey() {
                this.runQuote();
            }
        },
        created() {
            this.load();
        },
        beforeDestroy() {
            clearTimeout(this.quoteTimeout);
            if (this.quoteCancel) this.quoteCancel.cancel();
        }
    }
</script>
